import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { translations } from "../lib/i18n";

const AppContext = createContext(null);

// Display-only currencies. All prices stored & charged in EUR; this is for
// shop-front presentation. Rates fetched from frankfurter.app (ECB-sourced).
const CURRENCY_META = {
  EUR: { symbol: "€", locale: "de-DE", name: "Euro" },
  USD: { symbol: "$", locale: "en-US", name: "US Dollar" },
  GBP: { symbol: "£", locale: "en-GB", name: "British Pound" },
  BRL: { symbol: "R$", locale: "pt-BR", name: "Brazilian Real" },
  CAD: { symbol: "CA$", locale: "en-CA", name: "Canadian Dollar" },
  AUD: { symbol: "A$", locale: "en-AU", name: "Australian Dollar" },
  CHF: { symbol: "CHF", locale: "de-CH", name: "Swiss Franc" },
  SEK: { symbol: "kr", locale: "sv-SE", name: "Swedish Krona" },
  NOK: { symbol: "kr", locale: "nb-NO", name: "Norwegian Krone" },
  DKK: { symbol: "kr", locale: "da-DK", name: "Danish Krone" },
  PLN: { symbol: "zł", locale: "pl-PL", name: "Polish Zloty" },
  CZK: { symbol: "Kč", locale: "cs-CZ", name: "Czech Koruna" },
  JPY: { symbol: "¥", locale: "ja-JP", name: "Japanese Yen" },
  CNY: { symbol: "¥", locale: "zh-CN", name: "Chinese Yuan" },
  HKD: { symbol: "HK$", locale: "en-HK", name: "Hong Kong Dollar" },
  SGD: { symbol: "S$", locale: "en-SG", name: "Singapore Dollar" },
  INR: { symbol: "₹", locale: "en-IN", name: "Indian Rupee" },
  KRW: { symbol: "₩", locale: "ko-KR", name: "South Korean Won" },
  MXN: { symbol: "MX$", locale: "es-MX", name: "Mexican Peso" },
  ARS: { symbol: "AR$", locale: "es-AR", name: "Argentine Peso" },
  ZAR: { symbol: "R", locale: "en-ZA", name: "South African Rand" },
  AED: { symbol: "د.إ", locale: "en-AE", name: "UAE Dirham" },
};

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("kult_lang") || "en");
  const [currency, setCurrency] = useState(() => localStorage.getItem("kult_currency") || "EUR");
  const [fxRates, setFxRates] = useState({ EUR: 1 });
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem("kult_cart");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("kult_lang", lang);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("kult_currency", currency);
  }, [currency]);

  // Fetch FX rates once on mount (cached on the backend for 24h).
  useEffect(() => {
    const cachedRaw = localStorage.getItem("kult_fx_rates");
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached?.fetched_at && Date.now() - cached.fetched_at < 6 * 60 * 60 * 1000) {
          setFxRates({ EUR: 1, ...(cached.rates || {}) });
          return;
        }
      } catch {}
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fx/rates`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.rates) {
          setFxRates({ EUR: 1, ...d.rates });
          localStorage.setItem("kult_fx_rates", JSON.stringify({ rates: d.rates, fetched_at: Date.now() }));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("kult_cart", JSON.stringify(cart));
  }, [cart]);

  const t = useMemo(() => translations[lang], [lang]);

  /** Convert an EUR amount into the currently-selected currency (display only). */
  const convertPrice = useCallback(
    (eur) => {
      if (eur == null || isNaN(eur)) return 0;
      const rate = fxRates[currency] || 1;
      return Number(eur) * rate;
    },
    [currency, fxRates]
  );

  /** Format an EUR amount as a localized string in the active currency.
   *  decimals=null → auto (0 for JPY/KRW, 2 otherwise). */
  const formatPrice = useCallback(
    (eur, { decimals = null } = {}) => {
      const meta = CURRENCY_META[currency] || CURRENCY_META.EUR;
      const value = convertPrice(eur);
      const noDecimals = currency === "JPY" || currency === "KRW";
      const fr = decimals != null ? decimals : noDecimals ? 0 : 2;
      try {
        return new Intl.NumberFormat(meta.locale, {
          style: "currency",
          currency,
          minimumFractionDigits: fr,
          maximumFractionDigits: fr,
        }).format(value);
      } catch {
        return `${meta.symbol}${value.toFixed(fr)}`;
      }
    },
    [currency, convertPrice]
  );

  const currencies = useMemo(() => {
    return Object.keys(CURRENCY_META)
      .filter((c) => c === "EUR" || fxRates[c])
      .map((c) => ({ code: c, ...CURRENCY_META[c] }));
  }, [fxRates]);

  const addToCart = (product, size, color) => {
    setCart((prev) => {
      const idx = prev.findIndex(
        (i) => i.product_id === product.id && i.size === size && i.color === color
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          name_pt: product.name_pt,
          price: product.price,
          quantity: 1,
          size,
          color,
          image: product.images?.[0],
        },
      ];
    });
    setCartOpen(true);
  };

  const updateQty = (idx, qty) => {
    setCart((prev) => {
      const next = [...prev];
      if (qty <= 0) {
        next.splice(idx, 1);
      } else {
        next[idx] = { ...next[idx], quantity: qty };
      }
      return next;
    });
  };

  const removeItem = (idx) => {
    setCart((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearCart = () => setCart([]);

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const itemCount = cart.reduce((s, i) => s + i.quantity, 0);

  return (
    <AppContext.Provider
      value={{
        lang,
        setLang,
        t,
        currency,
        setCurrency,
        currencies,
        fxRates,
        convertPrice,
        formatPrice,
        cart,
        cartOpen,
        setCartOpen,
        addToCart,
        updateQty,
        removeItem,
        clearCart,
        subtotal,
        itemCount,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
